import { type FC, type ReactNode, type RefObject, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import { PipeSeparator } from './PipeSeparator.tsx';
import { FormattedPrice } from 'dibs-buyer-price/exports/FormattedPrice.ts';

import { type ProductPriceBadge_item$key } from './__generated__/ProductPriceBadge_item.graphql.ts';

import dibsCss from 'dibs-css';
import styles from '../styles/styles.scss';

import { PRODUCT_PRICE_TYPES, TEXT_PRICE_TYPES } from '../constants.ts';
import { messages } from './ProductPriceMessages.tsx';

const PriceComponentWrapper: FC<{
    children: ReactNode[];
    dataTn: string;
    ref: RefObject<HTMLParagraphElement | null>;
    isSmall?: boolean;
}> = ({ children, dataTn, ref }) => {
    return (
        <p
            data-tn={dataTn}
            ref={ref}
            className={classnames(
                styles.priceContainer,
                dibsCss.textBodySmallNormal,
                dibsCss.whitespaceBreakSpaces,
                dibsCss.wAuto,
                dibsCss.lineClamp2
            )}
        >
            {children}
        </p>
    );
};
const Price: FC<{
    price: number;
    currency: string;
    showPriceVariability: boolean | null | undefined;
}> = ({ price, currency, showPriceVariability }) => (
    <strong data-tn="price" className={dibsCss.textBodySmallHeavy}>
        {showPriceVariability ? messages.priceVariabilityLabel : null}
        <FormattedPrice value={price} currency={currency} />
    </strong>
);

const Discount: FC<{ percentageOff: string | null | undefined; netPrice?: boolean }> = ({
    percentageOff,
    netPrice,
}) =>
    percentageOff ? (
        <span
            data-tn="discount-amount"
            className={classnames(
                dibsCss.inlineFlex,
                netPrice ? dibsCss.textTradeprimary : dibsCss.textAlertsecondary,
                dibsCss.flexNowrap
            )}
        >
            {messages.textPercentageOff(percentageOff)}
        </span>
    ) : null;

const itemFragment = graphql`
    fragment ProductPriceBadge_item on Item
    @argumentDefinitions(
        isTrade: { type: "Boolean", defaultValue: false }
        pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
        priceBookName: { type: "String" }
    ) {
        displayPrice(page: $pageDisplayEnum, isTrade: $isTrade, priceBookName: $priceBookName) {
            convertedAmountList {
                amount
                currency
            }
            quantityDisplay
            percentageOff
            textType
            amountType
            showPriceVariability
        }
    }
`;

type Props = {
    currency: string;
    item: ProductPriceBadge_item$key;
    hideRecentSoldPrice?: boolean;
};

export const ProductPriceBadge: FC<Props> = ({
    item: itemRef,
    currency,
    hideRecentSoldPrice = false,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const displayPrice = item?.displayPrice?.[0];

    const {
        textType,
        amountType,
        convertedAmountList,
        quantityDisplay,
        percentageOff,
        showPriceVariability,
    } = displayPrice || {};

    const price = Math.floor(convertedAmountList?.find(c => c?.currency === currency)?.amount || 0);
    const priceRef = useRef<HTMLParagraphElement>(null);
    const quantityDisplayCopy = typeof quantityDisplay === 'string' ? ` ${quantityDisplay}` : null;
    const quantityDisplayNode = quantityDisplayCopy ? (
        <span className={dibsCss.flexWrap}>{quantityDisplayCopy}</span>
    ) : null;

    if (typeof textType === 'string' && TEXT_PRICE_TYPES.has(textType)) {
        if (textType === PRODUCT_PRICE_TYPES.PUR) {
            return (
                <p className={classnames(dibsCss.textBodySmallNormal, dibsCss.textNoir)}>
                    {messages.textPriceValue(PRODUCT_PRICE_TYPES.PUR)}
                </p>
            );
        }
        if (textType === PRODUCT_PRICE_TYPES.SOLD) {
            return (
                <p
                    className={classnames(dibsCss.flex, dibsCss.flexWrap, dibsCss.itemsCenter)}
                    data-tn={`price-${textType}`}
                >
                    <span
                        className={classnames(
                            dibsCss.textAlertsecondary,
                            dibsCss.textBodySmallHeavy,
                            dibsCss.whitespaceNowrap
                        )}
                    >
                        {messages.soldLabel}
                    </span>
                </p>
            );
        }
        const showPrice = !hideRecentSoldPrice && price > 0;
        return (
            <p
                className={classnames(dibsCss.flex, dibsCss.flexWrap, dibsCss.itemsCenter)}
                data-tn={`price-${textType}`}
            >
                <span
                    className={classnames(
                        dibsCss.textAlertsecondary,
                        dibsCss.textBodySmallHeavy,
                        dibsCss.whitespaceNowrap
                    )}
                >
                    {messages.textPriceValue(textType)}
                </span>
                {showPrice && (
                    <>
                        <PipeSeparator />
                        <span data-tn="price-retail" className={dibsCss.textBodySmallNormal}>
                            <del data-tn="price">
                                <FormattedPrice value={price} currency={currency} />
                                {quantityDisplayNode}
                            </del>
                        </span>
                    </>
                )}
            </p>
        );
    }

    if (!price || !amountType) {
        return null;
    }

    const wrapperProps = {
        ref: priceRef,
    };
    if (amountType === PRODUCT_PRICE_TYPES.SALE) {
        return (
            <PriceComponentWrapper dataTn="price-sale" {...wrapperProps}>
                <Price
                    price={price}
                    currency={currency}
                    showPriceVariability={showPriceVariability}
                />
                <>
                    {quantityDisplayNode}
                    {percentageOff && <PipeSeparator />}
                    <Discount percentageOff={percentageOff} />
                </>
            </PriceComponentWrapper>
        );
    }

    if (amountType === PRODUCT_PRICE_TYPES.NET) {
        return (
            <PriceComponentWrapper dataTn="price-net" {...wrapperProps}>
                <Price
                    price={price}
                    currency={currency}
                    showPriceVariability={showPriceVariability}
                />
                <>
                    {quantityDisplayNode}
                    {percentageOff && <PipeSeparator />}
                    <Discount percentageOff={percentageOff} netPrice />
                </>
            </PriceComponentWrapper>
        );
    }

    if (amountType === PRODUCT_PRICE_TYPES.RETAIL) {
        if (textType === PRODUCT_PRICE_TYPES.SOLD_PRICE) {
            return (
                <p
                    className={classnames(dibsCss.flex, dibsCss.flexWrap, dibsCss.itemsCenter)}
                    data-tn="price-similar-sold"
                >
                    <span
                        className={classnames(
                            dibsCss.textAlertsecondary,
                            dibsCss.textBodySmallHeavy,
                            dibsCss.whitespaceNowrap
                        )}
                    >
                        {messages.soldLabel}
                    </span>
                </p>
            );
        }
        return (
            <PriceComponentWrapper dataTn="price-retail" {...wrapperProps}>
                <Price
                    price={price}
                    currency={currency}
                    showPriceVariability={showPriceVariability}
                />
                {quantityDisplayNode}
            </PriceComponentWrapper>
        );
    }

    return null;
};
